import {
	formatAssistantTransportError,
	type UiChatMessage,
	useProtocolAssistantChat,
	useProtocolAssistantEnabled,
} from "@/hooks/useProtocolAssistant";
import { cn } from "@/lib/utils";
import { Loader2, Send } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

type Props = {
	className?: string;
	/** Dark agent-layer styling */
	variant?: "defi" | "agent";
};

/**
 * Multi-turn Protocol Assistant chat (v1 pattern).
 * Free-form only — no frequent-question chips.
 */
export function AssistantChat({ className, variant = "defi" }: Props) {
	const listId = useId();
	const bottomRef = useRef<HTMLDivElement>(null);
	const [input, setInput] = useState("");
	const [messages, setMessages] = useState<UiChatMessage[]>([]);
	const chat = useProtocolAssistantChat();
	const enabled = useProtocolAssistantEnabled();
	const agent = variant === "agent";

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, chat.isPending]);

	const send = async (text: string) => {
		const trimmed = text.trim();
		if (!trimmed || chat.isPending) return;
		const userMsg: UiChatMessage = {
			id: crypto.randomUUID(),
			role: "user",
			content: trimmed,
		};
		const next = [...messages, userMsg];
		setMessages(next);
		setInput("");
		try {
			const answer = await chat.mutateAsync(next);
			setMessages((prev) => [
				...prev,
				{
					id: crypto.randomUUID(),
					role: "assistant",
					content: answer,
				},
			]);
		} catch (err) {
			setMessages((prev) => [
				...prev,
				{
					id: crypto.randomUUID(),
					role: "assistant",
					content: formatAssistantTransportError(err),
				},
			]);
		}
	};

	return (
		<div className={cn("flex h-full min-h-0 flex-1 flex-col overflow-hidden", className)}>
			<div
				className={cn(
					"border-b px-4 py-3",
					agent ? "border-yellow-500/30" : "border-border/50",
				)}
			>
				<h2
					className={cn(
						"text-sm font-semibold",
						agent ? "font-mono text-yellow-400" : "font-display",
					)}
				>
					Protocol Assistant
				</h2>
				<p
					className={cn(
						"mt-1 text-[11px] leading-snug",
						agent ? "font-mono text-yellow-400/60" : "text-muted-foreground",
					)}
				>
					Read-only helper. Not financial advice. Cannot execute transactions.
					{!enabled
						? " Assistant canister not configured — replies will explain how to enable it."
						: null}
				</p>
			</div>

			<div
				id={listId}
				className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3"
				aria-live="polite"
			>
				{messages.length === 0 ? (
					<p
						className={cn(
							"text-sm",
							agent ? "font-mono text-yellow-400/65" : "text-muted-foreground",
						)}
					>
						Ask anything about CDP vaults, oracle prices, treasury, liquidation,
						or governance.
					</p>
				) : (
					messages.map((msg) => (
						<div
							key={msg.id}
							className={cn(
								"max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed",
								msg.role === "user"
									? agent
										? "ml-auto bg-yellow-400 text-black"
										: "ml-auto bg-primary text-primary-foreground"
									: agent
										? "border border-yellow-500/30 bg-yellow-400/10 font-mono text-yellow-100"
										: "bg-muted/80 text-foreground",
							)}
						>
							{msg.content}
						</div>
					))
				)}
				{chat.isPending ? (
					<div
						className={cn(
							"flex items-center gap-2 text-xs",
							agent ? "font-mono text-yellow-400/70" : "text-muted-foreground",
						)}
					>
						<Loader2 className="size-3.5 animate-spin" />
						Thinking…
					</div>
				) : null}
				<div ref={bottomRef} />
			</div>

			<form
				className={cn(
					"flex shrink-0 gap-2 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
					agent ? "border-yellow-500/30" : "border-border/50",
				)}
				onSubmit={(e) => {
					e.preventDefault();
					void send(input);
				}}
			>
				<textarea
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							void send(input);
						}
					}}
					rows={2}
					placeholder="Ask anything…"
					className={cn(
						"min-h-[2.75rem] flex-1 resize-none rounded-xl border px-3 py-2 text-sm outline-none",
						agent
							? "border-yellow-500/40 bg-black font-mono text-yellow-100 placeholder:text-yellow-400/40 focus:border-yellow-400"
							: "border-border bg-background focus:border-primary",
					)}
				/>
				<button
					type="submit"
					disabled={chat.isPending || !input.trim()}
					className={cn(
						"inline-flex size-11 shrink-0 items-center justify-center rounded-xl disabled:opacity-40",
						agent
							? "bg-yellow-400 text-black hover:bg-yellow-300"
							: "bg-primary text-primary-foreground",
					)}
					aria-label="Send"
				>
					<Send className="size-4" />
				</button>
			</form>
		</div>
	);
}
